/**
 * Native Data Context
 *
 * This module provides a React Context that can be used to pass data from the
 * "Native Data Source" to the page modules.
 *
 * "Native data" means data that is not provided by Contentful.
 * e.g., query string, API query result, etc.
 *
 * ## Useage
 *
 * ### Data source
 *
 * A data source should use the `setNativeData` to provide native data.
 *
 * Example:
 *
 * ```
 * // retrieve promoCode and promotion in some way
 * ...
 * // set to context
 * const { setNativeData } = React.useContext(NativeDataContext);
 * React.useEffect(() => {
 *  setNativeData('promo', {
 *    promoCode,
 *    promotion,
 *  });
 * }, [setNativeData, promoCode, promotion]);
 * ```
 *
 * The data key (the 1st argument to `setNativeData`) should be unique.
 *
 * The data type (the 2nd argument to `setNativeData`) should extend
 * `NativeDataRegistryEntry`, which means it should be a `Record<string, any>`.
 *
 * See the [confluence page](https://coursera.atlassian.net/wiki/spaces/ENT/pages/2827419669/Native+Data+Sources)
 * for more details.
 *
 * After creating a new data source, please document the following aspects in the document page:
 * - The data key
 * - The data type
 * - Guide for the content authors on how to use the data in Contentful
 *
 * ### Data consumer
 *
 * Use `getNativeData` to retrieve the native data.
 *
 * Example:
 *
 * ```
 * const { isDataSourceLoading, getNativeData } = React.useContext(NativeDataContext);
 * if (isDataSourceLoading) {
 *   const promo = getNativeData('promo');
 *   return <div>{promo.promoCode}</div>;
 * } else {
 *   return <div>Loading...</div>;
 * }
 * ```
 *
 * WARNING: DO NOT call `React.useContext(NativeDataContext)` in `PageAssembly`!!.
 * PageAssembly renders the data sources. If you call `React.useContext(NativeDataContext)` there,
 * it will cause an infinite re-render loop.
 */
import * as React from 'react';
import type { PropsWithChildren } from 'react';

import { type BILLING_TYPE_TO_DISPLAY_LABEL } from '@coursera-web/wes-common';

import type { WithPromoCodeToPromotionProps } from 'bundles/ent-website/types';

export type PromoDataRegistryEntry = Pick<
  WithPromoCodeToPromotionProps,
  'promoCode' | 'promotion' | 'enterprisePromotion'
>;

export type NativeDataKey = 'promo' | 'teamsUnitPrice' | 'courseraPlusPrices' | 'teamsUnitPriceWithTieredPricing';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type NativeDataRegistryEntry = Record<string, any>;

export type NativeDataRegistry = {
  [key in NativeDataKey]?: NativeDataRegistryEntry;
};

export type NativeDataParamsRegistry = {
  teamsUnitPriceWithTieredPricing?: {
    licenseCount?: number | null;
    billingType?: keyof typeof BILLING_TYPE_TO_DISPLAY_LABEL;
  };
  promo?: {
    defaultPromoCode?: string | null;
  };
};

export type NativeDataParamsKey = keyof NativeDataParamsRegistry;

export type NativeDataState = {
  isDataSourceLoading: () => boolean;
  setExpectedDataKeysRef: (keys: NativeDataKey[]) => void;
  isDataSourceExists: (key: NativeDataKey) => boolean;
  getNativeData: <T extends NativeDataRegistryEntry>(key: NativeDataKey) => T | undefined;
  setNativeData: (key: NativeDataKey, value: NativeDataRegistryEntry) => void;
  getNativeDataParams: <K extends NativeDataParamsKey>(key: K) => NativeDataParamsRegistry[K];
  setNativeDataParams: <K extends NativeDataParamsKey>(key: K, params: NativeDataParamsRegistry[K]) => void;
  // For params that are static (known at render time from Contentful) and never change at runtime.
  // Stored in a ref to avoid the re-render loop that `setNativeDataParams` would cause if called during render.
  setStaticDataParamsRef: <K extends NativeDataParamsKey>(key: K, params: NativeDataParamsRegistry[K]) => void;
};

export const NativeDataContext = React.createContext<NativeDataState>({
  isDataSourceLoading: () => false,
  setExpectedDataKeysRef: () => undefined,
  isDataSourceExists: () => false,
  getNativeData: () => undefined,
  setNativeData: () => undefined,
  getNativeDataParams: () => undefined,
  setNativeDataParams: () => undefined,
  setStaticDataParamsRef: () => undefined,
});

/**
 * NativeDataProvider
 * This component provides the native data context. It should wrap the top-level page.
 */
export function NativeDataProvider({ children }: PropsWithChildren<{}>) {
  const [nativeDataRegistry, setNativeDataRegistry] = React.useState<NativeDataRegistry>({});
  const [nativeDataParamsRegistry, setNativeDataParamsRegistry] = React.useState<NativeDataParamsRegistry>({});

  // Design note about the `expectedDataKeys`:
  //
  // To add a loading flag, one way is to add a `isLoading` state and have someone to set it to false.
  // However, this approach has several problems:
  // - the last data source should set it to false, but how to decide which one is the last one?
  // - what if no data source is used? Then the `PageAsembly` is responsible for setting it to false,
  //   but it may easily cause infinite re-render loop.
  //
  // To solve these problems, we use `expectedDataKeys` pattern instead.
  // The `expectedDataKeys` is a list of data keys that are expected to be set.
  // The `NativeContextProvider` will check if the data registries contain all the expected keys.
  // This approach can solve the previous problems perfectly.
  const expectedDataKeysRef = React.useRef<NativeDataKey[]>([]);
  const setExpectedDataKeysRef = (keys: NativeDataKey[]) => {
    expectedDataKeysRef.current = keys;
  };

  const setNativeData = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (key: NativeDataKey, data: NativeDataRegistryEntry) => {
      setNativeDataRegistry((prevState) => ({
        ...prevState,
        [key]: data,
      }));
    },
    []
  );

  const getNativeData = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    <T extends NativeDataRegistryEntry>(key: NativeDataKey) => nativeDataRegistry[key] as T | undefined,
    [nativeDataRegistry]
  );

  const setNativeDataParams = React.useCallback<NativeDataState['setNativeDataParams']>((key, params) => {
    setNativeDataParamsRegistry((prev) => ({ ...prev, [key]: params }));
  }, []);

  // Static params (known at render time, never change at runtime) are kept in a ref so they can be
  // written during render without triggering a re-render — mirroring the `expectedDataKeys` pattern.
  const staticDataParamsRef = React.useRef<NativeDataParamsRegistry>({});
  const setStaticDataParamsRef = React.useCallback<NativeDataState['setStaticDataParamsRef']>((key, params) => {
    staticDataParamsRef.current = { ...staticDataParamsRef.current, [key]: params };
  }, []);

  const getNativeDataParams = React.useCallback<NativeDataState['getNativeDataParams']>(
    (key) => {
      const staticParams = staticDataParamsRef.current[key];
      const dynamicParams = nativeDataParamsRegistry[key];
      if (staticParams == null && dynamicParams == null) return undefined;
      // Dynamic (runtime) params take precedence over static defaults for overlapping fields.
      return { ...staticParams, ...dynamicParams };
    },
    [nativeDataParamsRegistry]
  );

  const isDataSourceExist = (key: NativeDataKey) => expectedDataKeysRef.current.includes(key);

  // Check if all the expected data keys are set.
  // simply use the naive way since the number of data sources is usually less than 5.
  const isDataSourceLoading = () => expectedDataKeysRef.current.some((key) => !nativeDataRegistry[key]);

  return (
    <NativeDataContext.Provider
      value={{
        isDataSourceLoading,
        isDataSourceExists: isDataSourceExist,
        setExpectedDataKeysRef,
        setNativeData,
        getNativeData,
        setNativeDataParams,
        getNativeDataParams,
        setStaticDataParamsRef,
      }}
    >
      {children}
    </NativeDataContext.Provider>
  );
}

export const useNativeDataContext = (): NativeDataState => {
  const context = React.useContext(NativeDataContext);

  if (context == null) {
    throw new Error('useNativeDataContext should be used within NativeDataContext.Provider');
  }

  return context;
};

export const useNativeData = <T extends NativeDataRegistryEntry>(key: NativeDataKey) => {
  const { getNativeData } = useNativeDataContext();

  return getNativeData<T>(key);
};

export const useNativeDataParams = () => {
  const { getNativeDataParams, setNativeDataParams } = useNativeDataContext();

  return {
    getParams: getNativeDataParams,
    setParams: setNativeDataParams,
  };
};
